import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ChangeEvent,
    createContext,
    Fragment,
    FunctionComponent,
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from 'react';
import { DateRangePicker } from '@/components/DatePicker/DateRangePicker';
import { Select } from '@/components/Select/Select';
import { BookingChannelTabs } from '@/pageContents/productConfiguration/ProductConfigurationDetail/BookingChannelTabs';
import { BookingOptionTabs } from '@/pageContents/productConfiguration/ProductConfigurationDetail/BookingOptionTabs';
import { ProductConfiguration, ProductType } from '@/models/product';
import { DateRange } from '@/models/general';
import { SectionCard } from './SectionCard';
import { NumberInput } from '@/components/NumberInput/NumberInput';

type ProductConfigurationProps = {
    baseConfiguration: ProductConfiguration;
};

export const ProductConfigurationGeneralConfiguration: FunctionComponent<
    ProductConfigurationProps
> = ({ baseConfiguration }) => {
    const [productConfiguration, setProductConfiguration] =
        useState<ProductConfiguration>({ ...baseConfiguration });

    const onDateRangeChange =
        (configField: keyof ProductConfiguration) => (dateRange: DateRange) => {
            const [from, to] = dateRange;

            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: {
                    from,
                    to,
                },
            }));
        };

    const onNumberFieldChange =
        (configField: keyof ProductConfiguration) =>
        (nexValue: number | null) => {
            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: nexValue,
            }));
        };

    const onProductTypeOverrideChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setProductConfiguration((prevState) => ({
            ...prevState,
            productTypeOverride: value === '-' ? null : (value as ProductType),
        }));
    };

    const onTextFieldChange =
        (configField: keyof ProductConfiguration, asStringArray = false) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            const nextValue: string | string[] = asStringArray
                ? value.split('\n')
                : value;

            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: nextValue,
            }));
        };

    useEffect(() => {
        setProductConfiguration({ ...baseConfiguration });
    }, [baseConfiguration]);

    return (
        <GeneralConfigurationContextProvider
            productConfiguration={productConfiguration}
        >
            <Stack spacing={8}>
                <SectionCard title='Allgemeine Daten'>
                    <Grid spacing={6} container>
                        <Grid item md={12}>
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                }}
                                control={
                                    <Checkbox
                                        checked={productConfiguration.enabled}
                                        onChange={(_event, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    enabled: checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='Produkt für Verkauf aktivieren (alle Buchungskanäle)'
                            />
                        </Grid>
                        <Grid item md={6}>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Verkaufszeitraum festlegen (alle
                                    Buchungskanäle)
                                </Typography>

                                <Box>
                                    <Typography>
                                        In welchem Zeitraum soll das Ticket im
                                        Webshop zum Kauf angeboten werden?
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '500',
                                        }}
                                    >
                                        Wenn von SKIDATA / AXESS ein früheres
                                        Enddatum an uns übermittelt wird, so
                                        greift dieses Datum
                                    </Typography>
                                </Box>

                                <DateRangePicker
                                    value={[
                                        productConfiguration
                                            .startDateValidityRange.from,
                                        productConfiguration
                                            .startDateValidityRange.to,
                                    ]}
                                    onChange={onDateRangeChange(
                                        'startDateValidityRange'
                                    )}
                                    fromText='von'
                                    toText='bis'
                                />
                            </Stack>
                        </Grid>
                        <Grid item md={6}>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Gültigkeitszeitraum des Tickets festlegen
                                    (alle Buchungskanäle)
                                </Typography>

                                <Box>
                                    <Typography>
                                        Für welchen Zeitraum kann das Ticket
                                        gebucht und genutzt werden?
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '500',
                                        }}
                                    >
                                        Wenn von SKIDATA / AXESS ein früheres
                                        Enddatum an uns übermittelt wird als
                                        hier angegeben, so greift das früher
                                        endende Datum.
                                    </Typography>
                                </Box>

                                <DateRangePicker
                                    value={[
                                        productConfiguration.validityRange.from,
                                        productConfiguration.validityRange.to,
                                    ]}
                                    onChange={onDateRangeChange(
                                        'validityRange'
                                    )}
                                    fromText='von'
                                    toText='bis'
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard title='Webshop'>
                    <Grid spacing={6} container>
                        <Grid md={12} item>
                            <Stack
                                sx={{
                                    width: '50%',
                                }}
                                spacing={4}
                            >
                                <Stack spacing={2}>
                                    <Typography
                                        variant='subtitle1'
                                        gutterBottom
                                    >
                                        Webshop-Kategorie Ihres Tickets ändern
                                    </Typography>

                                    <Select
                                        onChange={onProductTypeOverrideChange}
                                        value={
                                            productConfiguration.productTypeOverride ||
                                            '-'
                                        }
                                        label='Webshop Kategorie wählen'
                                    >
                                        <option value='-'>
                                            Ziel-Kategorie wählen
                                        </option>
                                        <option value={ProductType.DAY}>
                                            Tages- & / Mehrtagesticket{' '}
                                        </option>
                                        <option value={ProductType.SEASON}>
                                            Saison - & / Jahresticket
                                        </option>
                                    </Select>
                                </Stack>

                                {productConfiguration.productTypeOverride ===
                                    ProductType.DAY && (
                                    <Fragment>
                                        <Typography>
                                            Bei Ziel-Kategorie „Tages- &
                                            Mehrtagestickets“ geben Sie hier an,
                                            unter welcher Anzahl Tage (=Dauer)
                                            das Ticket im Shop ausgegeben werden
                                            darf.
                                        </Typography>
                                        <NumberInput
                                            onChange={onNumberFieldChange(
                                                'durationOverride'
                                            )}
                                            value={
                                                productConfiguration.durationOverride
                                            }
                                            label='Anzahl Tage'
                                        />
                                    </Fragment>
                                )}
                            </Stack>
                        </Grid>

                        <Grid item md={6}>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Freigabegruppe für Spezialtarif hinterlegen
                                    (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Geben Sie hier den exakten Namen ihrer
                                    Freigabegruppe ein. Damit machen Sie diese
                                    Ticket im Webshop nur noch für die Gäste
                                    sichtbar und buchbar, die von Ihnen einen
                                    Freigabe-Code dieser Gruppe erhalten. (Mehr
                                    dazu im Menü &quot;Spezialtarife&quot;). Bei
                                    Eingabe mehrerer Freigabe-Gruppen: Bitte je
                                    Zeile einen „Namen der Freigabegruppe“, ohne
                                    Komma, eingebe
                                </Typography>

                                <TextField
                                    label='Bezeichnung der Tarifgruppe hinterlegen'
                                    value={productConfiguration.keycardCodes?.join(
                                        '\n'
                                    )}
                                    onChange={onTextFieldChange(
                                        'keycardCodes',
                                        true
                                    )}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>

                        <Grid item md={6}>
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Tags für Standard-Webshop hinterlegen
                                </Typography>

                                <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    Bei Übergabe der hier hinterlegten „Tags“
                                    bei Ihrer Shop-URL werden nur die Angebote
                                    angezeigt, die sich hinter dem Tag / den
                                    Tags befinden. Bei Eingabe mehreren Tags
                                    bitte einen Tag je Zeile speichern, ohne
                                    Komma eingeben.
                                </Typography>

                                <TextField
                                    label='Tags für Standard-Webshop hinterlegen'
                                    value={productConfiguration.tags?.join(
                                        '\n'
                                    )}
                                    onChange={onTextFieldChange('tags', true)}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard title='Ticket Daten'>
                    <Grid spacing={6} container>
                        <Grid xs={12} md={6} item>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Infofeld DEUTSCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Infos werden in Form
                                    eines Info-Buttons bei diesem Ticket
                                    eingeblendet, welche der Gast mir Klick
                                    darauf öffnet.
                                </Typography>

                                <TextField
                                    label='Info Feld DEUTSCH (alles Buchungskanäle)'
                                    onChange={onTextFieldChange('infoTextDE')}
                                    value={productConfiguration.infoTextDE}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>
                        <Grid xs={12} md={6} item>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Infofeld ENGLISCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Infos werden in Form
                                    eines Info-Buttons bei diesem Ticket
                                    eingeblendet, welche der Gast mir Klick
                                    darauf öffnet.
                                </Typography>

                                <TextField
                                    label='Info Feld ENGLISCH (alles Buchungskanäle)'
                                    onChange={onTextFieldChange('infoTextEN')}
                                    value={productConfiguration.infoTextEN}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>

                        <Grid md={6} item>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Ticket-Filter DEUTSCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Je Eingabe wird ein Einzelauswahl-Filter im
                                    Webshop ausgegeben, womit Sie Ihren Gast
                                    noch besser zum Wunsch-Ticket führen können.
                                    Ein Filtername kann bei mehreren Tickets
                                    hinterlegt werden. Pro Zeile einen Filtertag
                                    eingeben, ohne Komma.
                                </Typography>

                                <TextField
                                    label='Ticket Filter DEUTSCH (Alle Buchungskanäle)'
                                    value={productConfiguration.filterTagsDE}
                                    onChange={onTextFieldChange('filterTagsDE')}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>

                        <Grid md={6} item>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Ticket-Filter ENGLISCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Je Eingabe wird ein Einzelauswahl-Filter im
                                    Webshop ausgegeben, womit Sie Ihren Gast
                                    noch besser zum Wunsch-Ticket führen können.
                                    Ein Filtername kann bei mehreren Tickets
                                    hinterlegt werden. Pro Zeile einen Filtertag
                                    eingeben, ohne Komma.
                                </Typography>

                                <TextField
                                    label='Ticket Filter DEUTSCH (Alle Buchungskanäle)'
                                    value={productConfiguration.filterTagsEN}
                                    onChange={onTextFieldChange('filterTagsEN')}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard title='Kontingent'>
                    <Grid spacing={6} container>
                        <Grid md={6} item>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Ticket-Kontingent (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Maximales Kontingent für dieses Ticket pro
                                    Startdatum. Bei Mehrtagestickets wird das
                                    Kontingent an jedem Tag gezählt.
                                    Stornierungen werden dem Kontingent wieder
                                    gutgeschrieben. Hinweis: Ein
                                    Gesamtkontingent pro Startdatum können Sie
                                    über das starjack-Team hinterlegen lassen!
                                </Typography>

                                <NumberInput
                                    onChange={onNumberFieldChange(
                                        'maxTicketsPerStartDay'
                                    )}
                                    value={
                                        productConfiguration.maxTicketsPerStartDay
                                    }
                                    label='Ticket Kontingent'
                                />
                            </Stack>
                        </Grid>

                        <Grid md={6} item>
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Kontingent Schwellenwert
                                </Typography>

                                <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    Ab welchem Kontingent-Restwert soll Ihr
                                    Kontingent im Webshop angezeigt werden? Wenn
                                    kein Wert hinterlegt ist, wird das gesamte
                                    Kontingent ab Aktivierung angezeigt.
                                </Typography>

                                <NumberInput
                                    onChange={onNumberFieldChange(
                                        'contingentsThreshold'
                                    )}
                                    value={
                                        productConfiguration.contingentsThreshold
                                    }
                                    label='Kontingent Schwellenwert'
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </SectionCard>

                <SectionCard
                    title='Erklärung der Buchungs-Optionen für Ihre Onlinetickets'
                    hideActions
                >
                    <Typography variant='subtitle1' gutterBottom>
                        Als Standard-Buchungsoption ist die Ticketbuchung auf
                        Keycards („Keycard“) aktiviert. Optional können
                        Unternehmen mit SKIDATA-Zutrittssystem ihren Gästen hier
                        weitere Buchungsoptionen aktivieren. Bitte stellen Sie
                        vorab sicher, dass die technischen Voraussetzungen
                        gegeben sind und dass auch alle Kassa-Mitarbeiter über
                        ggf. anfallende Aufgaben (Ticket Erstellungen / Versand
                        etc.) informiert
                    </Typography>

                    <BookingChannelTabs />
                </SectionCard>

                <SectionCard title='Aktivierung der Buchungs-Optionen für Ihre Onlinetickets nach Verkaufskanal'>
                    <Typography variant='subtitle1' gutterBottom>
                        Definieren Sie hier die Buchungsoptionen für dieses
                        Ticket. Nach dem Speichern sind die Optionen sofort im
                        jeweiligen Verkaufskanal verfügbar.
                    </Typography>
                    <BookingOptionTabs
                        productConfiguration={productConfiguration}
                        onProductConfigurationChange={(newConfiguration) => {
                            setProductConfiguration(newConfiguration);
                        }}
                    />
                </SectionCard>
            </Stack>
        </GeneralConfigurationContextProvider>
    );
};

export type GeneralConfigurationContextType = {
    productConfiguration: ProductConfiguration;
};

const GeneralConfigurationContext =
    createContext<GeneralConfigurationContextType>({
        productConfiguration: null,
    });
const GeneralConfigurationContextProvider: FunctionComponent<
    PropsWithChildren<{
        productConfiguration: ProductConfiguration;
    }>
> = ({ productConfiguration, children }) => {
    return (
        <GeneralConfigurationContext.Provider
            value={{ productConfiguration: productConfiguration }}
        >
            {children}
        </GeneralConfigurationContext.Provider>
    );
};

export const useGeneralProductConfigurationContext = () => {
    return useContext(GeneralConfigurationContext);
};
